import { useEffect, useState } from "react";
import {
  DateRangeInput,
  type DateRange,
} from "src/components/Inputs/DateRangeInput";
import { SearchAndFilterTypes } from "src/constants";
import { CommonFilterProps, FormFilter } from "../CommonFilters";

type DateFilterKeys = {
  daysAgo: SearchAndFilterTypes;
  dateFrom: SearchAndFilterTypes;
  dateTo: SearchAndFilterTypes;
};

type DateFilterProps = CommonFilterProps & {
  label: string;
  description: string;
  filterKeys: DateFilterKeys;
  direction?: "past" | "future";
};

function getSelectedDateRange(
  filterSettings: CommonFilterProps["filterSettings"],
  filterKeys: DateFilterKeys,
  direction: "past" | "future"
): DateRange | null {
  const daysAgo = filterSettings.getOne(filterKeys.daysAgo);
  const dateFrom = filterSettings.getOne(filterKeys.dateFrom);
  const dateTo = filterSettings.getOne(filterKeys.dateTo);

  if (daysAgo) {
    if (direction === "future") {
      switch (daysAgo) {
        case "0":
          return { key: "Today" };
        case "7":
          return { key: "Next7Days" };
        case "30":
          return { key: "Next30Days" };
        default:
          return { key: "CustomNextDays", days: daysAgo };
      }
    }

    switch (daysAgo) {
      case "1":
        return { key: "LastDay" };
      case "7":
        return { key: "Last7Days" };
      case "30":
        return { key: "Last30Days" };
      default:
        return { key: "CustomLastDays", days: daysAgo };
    }
  }

  if (dateFrom || dateTo) {
    return {
      key: "CustomDateRange",
      dateFrom: dateFrom ?? "",
      dateTo: dateTo ?? "",
    };
  }

  return null;
}

export const DateFilter = ({
  filterSettings,
  label,
  description,
  filterKeys,
  direction = "past",
}: DateFilterProps) => {
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(
    () => getSelectedDateRange(filterSettings, filterKeys, direction)
  );

  useEffect(() => {
    setSelectedDateRange(
      getSelectedDateRange(filterSettings, filterKeys, direction)
    );
  }, [filterSettings, filterKeys, direction]);

  const clearAllDateParams = () => {
    filterSettings.set(filterKeys.daysAgo);
    filterSettings.set(filterKeys.dateFrom);
    filterSettings.set(filterKeys.dateTo);
  };

  const handleChange = (dateRange: DateRange | null) => {
    setSelectedDateRange(dateRange);
    clearAllDateParams();

    if (!dateRange) return;

    switch (dateRange.key) {
      case "LastDay":
        filterSettings.set(filterKeys.daysAgo, "1");
        break;
      case "Last7Days":
      case "Next7Days":
        filterSettings.set(filterKeys.daysAgo, "7");
        break;
      case "Last30Days":
      case "Next30Days":
        filterSettings.set(filterKeys.daysAgo, "30");
        break;
      case "Today":
        filterSettings.set(filterKeys.daysAgo, "0");
        break;
      case "CustomLastDays":
      case "CustomNextDays":
        if (dateRange.days && Number(dateRange.days) > 0) {
          filterSettings.set(filterKeys.daysAgo, dateRange.days);
        }
        break;
      case "CustomDateRange":
        if (dateRange.dateFrom) {
          filterSettings.set(filterKeys.dateFrom, dateRange.dateFrom);
        }
        if (dateRange.dateTo) {
          filterSettings.set(filterKeys.dateTo, dateRange.dateTo);
        }
        break;
    }
  };

  return (
    <FormFilter label={label}>
      <DateRangeInput
        label={description}
        direction={direction}
        selectedDateRange={selectedDateRange}
        onChange={handleChange}
      />
    </FormFilter>
  );
};
